/**
 * Voie c) « Scan de code-barres » (specs §6.4).
 *
 * Enchaînement imposé par les specs, et respecté ici de bout en bout :
 *   **scan → produit trouvé (perso d'abord, puis Open Food Facts) → écran de
 *   quantité → validation**, cible sous les 5 secondes.
 *
 * ⚠️ La priorité de la base personnelle est **côté serveur**
 * (`GET /api/foods/barcode/:code`) : quand une fiche porte ce code-barres, OFF
 * n'est pas appelé du tout. Le deuxième scan d'un produit habituel ne fait donc
 * sortir aucun octet du VPS — c'est ce qui rend le geste fiable en cuisine, avec
 * une connexion médiocre.
 *
 * 🔴 **Et depuis la V2 (étape 4), la même question est posée à la réplique
 * AVANT d'être posée au réseau** — c'est le « scanner un produit déjà présent
 * dans la base personnelle » du §3.1.
 *
 * Ce n'est pas une seconde règle de priorité : c'est la **même**, appliquée un
 * cran plus tôt. Le serveur répond déjà « la fiche perso gagne » ; encore
 * faut-il pouvoir l'atteindre. En posant la question au cache d'abord, on ne
 * rend pas l'aller-retour plus rapide, on le **supprime** — et un produit connu
 * se scanne alors en mode avion exactement comme au comptoir.
 *
 * ⚠️ **Un code inconnu de la réplique repart au serveur**, sans exception : lui
 * seul connaît Open Food Facts, et lui seul fait autorité sur ce que la base
 * contient réellement. Hors ligne, cet appel échoue et l'écran propose de créer
 * la fiche à la main avec ce code — ce que le §6.4 des specs prévoit déjà, et
 * ce que le §3.1 annonce comme la limite du hors ligne.
 *
 * ⚠️ **Code-barres inconnu → création immédiate d'une fiche pré-remplie** avec
 * ce code (specs §6.4). Pas de cul-de-sac : c'est précisément le moment où
 * l'utilisateur a le produit en main et peut lire l'étiquette.
 *
 * ⚠️ Ce panneau n'est monté **que** lorsque l'onglet Scanner est sélectionné —
 * la feuille d'ajout, elle, reste montée en permanence pour être instantanée
 * (§6.2). Sans cette garde, la caméra serait demandée au lancement de l'app, ce
 * que le §2 interdit explicitement.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { fetchFoods, lookupBarcode } from '../../api/foods';
import { findFoodByBarcode } from '../../lib/localSearch';
import { BarcodeScanner } from '../../components/scanner/BarcodeScanner';
import { Card } from '../../components/nourri';
import { Button, ErrorNote, Hint } from '../../components/ui/primitives';
import { judgeManualCode, normalizeBarcode } from '../../lib/barcode';
import type { FoodPick } from './addTypes';

/**
 * Saisie **manuelle** du code-barres — demandée après usage.
 *
 * Trois situations où la caméra ne suffit pas, et qui se présentent toutes en
 * cuisine : une étiquette froissée, déchirée ou sous plastique brillant que le
 * décodeur refuse ; un produit déjà transvasé dont on garde l'emballage ; un
 * appareil qui refuse l'accès caméra. Sans cette voie, chacune de ces situations
 * était un cul-de-sac — le §5.2 prévoyait pourtant depuis l'étape 6 de
 * « pré-remplir une fiche depuis un code saisi à la main », sans qu'aucun geste
 * n'y mène.
 *
 * ⚠️ La somme de contrôle **avertit sans bloquer** (cf. `lib/barcode.ts`) : un
 * code recopié d'une étiquette abîmée peut être juste sans que notre calcul le
 * confirme. Une longueur aberrante, elle, ne déclenche aucun aller-retour — ce
 * n'est pas un code, c'est une saisie en cours.
 *
 * ⚠️ Le champ est un `text` en `inputMode="numeric"`, pas un `type="number"` :
 * un champ nombre perd les zéros de tête (« 0123456789012 ») et affiche des
 * flèches d'incrément qui n'ont aucun sens sur un identifiant.
 */
function ManualCodeForm({
  onSubmit,
  disabled,
}: {
  onSubmit: (code: string) => void;
  disabled: boolean;
}) {
  const [raw, setRaw] = useState('');
  const verdict = judgeManualCode(raw);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <span className="n-field__label">Code-barres saisi à la main</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            value={raw}
            onChange={(event) => {
              /*
               * ⚠️ Le plafond porte sur les **chiffres retenus**, pas sur la
               * longueur tapée — et surtout pas sur un `maxLength` de l'input.
               * Un `maxLength` tronque la saisie *avant* qu'on en retire les
               * séparateurs : coller « 3017 6204 2200 3 » depuis une étiquette
               * perdait alors les derniers chiffres. Constaté au banc.
               */
              setRaw(normalizeBarcode(event.target.value).slice(0, 14));
            }}
            inputMode="numeric"
            autoComplete="off"
            placeholder="3017620422003"
            className="ui-input"
            style={{
              flex: 1,
              minWidth: 0,
              fontVariantNumeric: 'tabular-nums',
              boxSizing: 'border-box',
            }}
          />
          <Button
            disabled={disabled || verdict.state === 'empty' || verdict.state === 'wrong-length'}
            onClick={() => {
              onSubmit(normalizeBarcode(raw));
            }}
            style={{ flexShrink: 0 }}
          >
            Chercher
          </Button>
        </div>
      </label>
      {verdict.state === 'bad-checksum' && (
        <Hint>
          La clé de contrôle de ce code ne tombe pas juste — vérifiez la saisie. On peut
          chercher quand même : certains produits portent un code hors norme.
        </Hint>
      )}
      {verdict.state === 'wrong-length' && (
        <Hint>Un code-barres alimentaire fait 8, 12 ou 13 chiffres.</Hint>
      )}
    </div>
  );
}

export function ScanPanel({
  onPick,
  onUnknownCode,
}: {
  onPick: (pick: FoodPick) => void;
  /** Code lu mais inconnu partout : proposer une fiche pré-remplie (§6.4). */
  onUnknownCode: (code: string) => void;
}) {
  const [scanning, setScanning] = useState(true);
  const [unknown, setUnknown] = useState<string | null>(null);

  /*
   * La réplique de la base personnelle (§3.1) — la **même** entrée de cache que
   * la Bibliothèque et l'éditeur de recettes. Elle est persistée, donc présente
   * au démarrage même sans réseau.
   *
   * ⚠️ Ce n'est pas une requête « pour le scanner » : lui donner une clé à elle
   * ferait deux copies de la base dans le cache, dont l'une pourrait être plus
   * fraîche que l'autre. Une base, un cache, une vérité.
   */
  const foods = useQuery({ queryKey: ['foods', ''], queryFn: () => fetchFoods('') });

  const lookup = useMutation({
    mutationFn: lookupBarcode,
    onSuccess: (result) => {
      // Priorité de la base personnelle (specs §7.2) : le serveur l'a déjà
      // appliquée, on suit son verdict sans le rejouer.
      if (result.personal) {
        onPick({ kind: 'food', food: result.personal });
        return;
      }
      if (result.off) {
        onPick({ kind: 'off', product: result.off });
        return;
      }
      setUnknown(result.code);
    },
  });

  /**
   * 🔴 **Le point d'entrée unique des deux voies de saisie d'un code** — caméra
   * et clavier. Les faire diverger ici reproduirait le defaut que
   * `lib/barcode.ts` existe pour empecher : les deux voies d'entree d'un code
   * doivent le juger pareil.
   *
   * ⚠️ **La réplique d'abord, le réseau ensuite.** Un produit déjà en base est
   * servi sans aucune requête — instantané au comptoir, et disponible en mode
   * avion. Tout le reste part au serveur, qui seul connaît Open Food Facts.
   */
  const resoudre = (code: string) => {
    const connu = findFoodByBarcode(foods.data ?? [], code);
    if (connu) {
      onPick({ kind: 'food', food: connu });
      return;
    }
    lookup.mutate(code);
  };

  /*
   * ⚠️ `useCallback` : `BarcodeScanner` relance son effet — donc la caméra —
   * quand cette fonction change d'identité. Sans la stabiliser, chaque rendu
   * du panneau rouvrirait le flux vidéo.
   */
  const onDetected = useCallback(
    (code: string) => {
      setScanning(false);
      const connu = findFoodByBarcode(foods.data ?? [], code);
      if (connu) {
        onPick({ kind: 'food', food: connu });
        return;
      }
      lookup.mutate(code);
    },
    // ⚠️ `resoudre` n'est pas listée ici : elle est recréée à chaque rendu, et
    // `BarcodeScanner` rouvre la caméra quand cette fonction change d'identité.
    // Le corps est donc recopié, avec les seules dépendances qui comptent.
    [foods.data, lookup, onPick],
  );

  const restart = () => {
    setUnknown(null);
    lookup.reset();
    setScanning(true);
  };

  const submitManual = (code: string) => {
    setScanning(false);
    setUnknown(null);
    resoudre(code);
  };

  if (scanning) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <BarcodeScanner onDetected={onDetected} />
        {/*
          La saisie manuelle vit **sous la caméra**, pas derrière un onglet de
          plus : c'est le repli qu'on cherche au moment précis où le scan
          n'aboutit pas, produit en main. Un geste supplémentaire pour y accéder
          annulerait l'intérêt.
        */}
        <ManualCodeForm onSubmit={submitManual} disabled={lookup.isPending} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {lookup.isPending && <Hint>Code lu — recherche du produit…</Hint>}

      {lookup.isError && (
        <>
          <ErrorNote>{lookup.error.message}</ErrorNote>
          <Button variant="quiet" onClick={restart}>
            Scanner à nouveau
          </Button>
        </>
      )}

      {unknown !== null && (
        <>
          <Card>
            <div className="n-eyebrow">Code-barres inconnu</div>
            <p className="n-hint" style={{ marginTop: 8 }}>
              {unknown} — ni dans votre base, ni chez Open Food Facts.
            </p>
          </Card>
          <Button
            onClick={() => {
              onUnknownCode(unknown);
            }}
          >
            Créer cet aliment avec ce code-barres
          </Button>
          <Button variant="quiet" onClick={restart}>
            Scanner à nouveau
          </Button>
          <Hint>
            La fiche créée servira aussi pour les prochains scans : ce code ne sera plus jamais
            inconnu.
          </Hint>
        </>
      )}
    </div>
  );
}
